import React, {
  ChangeEventHandler,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { generatePath, useHistory, useLocation } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useToasts } from 'react-toast-notifications';
import axios from 'axios';
import { ShowProductResponse } from '../../../../../setup/apiTypes/warehouse';
import routes from '../../../../../setup/consts/routes';
import apiRoutes from '../../../../../setup/consts/apiRoutes';
import { makeApiUrl } from '../../../../../utils/reactQuery';
import Button from '../../../../../components/atoms/Button';
import { ButtonKind } from '../../../../../components/atoms/Button/enums';
import Badge from '../../../../../components/atoms/Badge';
import { BadgeTone } from '../../../../../components/atoms/Badge/enums';
import ScCard from '../../../../../components/atoms/ScCard';
import ScStat from '../../../../../components/molecules/ScStat';
import ScSectionHeader from '../../../../../components/molecules/ScSectionHeader';
import ProductMonogram from '../../../../../components/atoms/ProductMonogram';
import {
  IconChevronLeft,
  IconChevronRight,
  IconEdit,
  IconUpload,
  IconInfo,
  IconWarehouse,
  IconArrowForward,
  IconBox,
  WarningIcon,
} from '../../../../../assets/svg/icons';

type Props = { product: ShowProductResponse['product'] | null };


const TONE_COLOR: Record<string, string> = {
  success: 'var(--brand-green)',
  danger:  'var(--danger)',
  warning: 'var(--warning)',
  info:    'var(--info)',
  neutral: 'var(--muted-2)',
};


/* ─── KV row used in pricing / stock / details cards ─── */
const KV: React.FC<{ k: string; v: React.ReactNode; last?: boolean }> = ({ k, v, last }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '8px 0', gap: 12,
    borderBottom: last ? 'none' : '1px solid var(--line)',
  }}>
    <span style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)', fontWeight: 500 }}>{k}</span>
    <span style={{ fontSize: 'var(--t-sm)', fontWeight: 600, textAlign: 'right', minWidth: 0 }}>{v}</span>
  </div>
);

const Row2sm: React.FC<{ label: string; value: React.ReactNode; last?: boolean }> = ({ label, value, last }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', padding: '7px 0',
    borderBottom: last ? 'none' : '1px solid var(--line)', fontSize: 'var(--t-sm)',
  }}>
    <span style={{ color: 'var(--muted)', fontWeight: 500 }}>{label}</span>
    <span style={{ fontWeight: 600 }}>{value}</span>
  </div>
);

/* ─── Main ProductDetails component ─── */
const ProductDetails: React.FC<Props> = ({ product }) => {
  const history = useHistory();
  const location = useLocation();
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { addToast } = useToasts();
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const TIMELINE_MOCK = [
    { label: intl.formatMessage({ id: 'warehouse.activityLog.orderPlaced',  defaultMessage: 'Order placed' }),   sub: 'TAG · 1 unit (Picked Up)',         tone: 'success', active: true,  date: '24/05/2026 · 2:11 PM'  },
    { label: intl.formatMessage({ id: 'warehouse.activityLog.stockReturned',defaultMessage: 'Stock returned' }), sub: '1 unit returned from failed order', tone: 'info',    active: false, date: '24/05/2026 · 9:02 AM'  },
    { label: intl.formatMessage({ id: 'warehouse.activityLog.restocked',    defaultMessage: 'Restocked' }),      sub: '120 units received from supplier',  tone: 'success', active: false, date: '23/05/2026 · 4:48 PM'  },
    { label: intl.formatMessage({ id: 'warehouse.activityLog.lowStockAlert',defaultMessage: 'Low stock alert' }),sub: 'Threshold of 5 units crossed',     tone: 'warning', active: false, date: '21/05/2026 · 8:14 AM'  },
    { label: intl.formatMessage({ id: 'warehouse.activityLog.skuCreated',   defaultMessage: 'SKU created' }),    sub: 'By Ahmed Saber',                    tone: 'neutral', active: false, date: '24/01/2024 · 10:00 AM' },
  ];

  // Old deep-links used ?edit=1 to open the (retired) edit drawer — send
  // them to the real edit page instead.
  useEffect(() => {
    if (product && new URLSearchParams(location.search).get('edit') === '1') {
      history.replace(
        generatePath(routes.EDIT_PRODUCT, { productId: product.id })
      );
    }
  }, [history, location.search, product]);

  const openEdit = () => {
    if (product) {
      history.push(generatePath(routes.EDIT_PRODUCT, { productId: product.id }));
    }
  };

  const onCoverImageChange: ChangeEventHandler<HTMLInputElement> = async (
    event
  ) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file || !product) {
      return;
    }

    const formData = new FormData();
    formData.append('coverImage', file);
    setUploadingImage(true);

    try {
      await axios.post(
        makeApiUrl(apiRoutes.UPLOAD_PRODUCT_IMAGES(product.id)),
        formData,
        { headers: { 'Content-type': 'multipart/form-data' } }
      );
      await queryClient.invalidateQueries(
        apiRoutes.GET_WAREHOUSE_PRODUCT(product.id)
      );
      addToast(
        intl.formatMessage({
          id: 'warehouse.productUpdatedMessage',
          defaultMessage: 'Product updated successfully.',
        }),
        { appearance: 'success' }
      );
    } catch (error) {
      addToast(
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ??
          intl.formatMessage({
            id: 'warehouse.uploadImageFailed',
            defaultMessage: 'Failed to upload the image',
          }),
        { appearance: 'error' }
      );
    } finally {
      setUploadingImage(false);
    }
  };

  if (!product) {
    return <p style={{ color: 'var(--muted)', padding: 'var(--s-5)' }}>{intl.formatMessage({ id: 'warehouse.couldntLoadProduct', defaultMessage: "Couldn't load product data." })}</p>;
  }

  const {
    name, sku, categoryName, coverImage, images,
    sellingPrice, costPrice, createdOn, description,
    specification, warehouseProducts,
  } = product;

  const onHand = Array.isArray(warehouseProducts)
    ? (warehouseProducts as any[]).reduce((s: number, w: any) => s + (w?.availableQuantity ?? 0), 0)
    : 0;

  const status: 'ok' | 'low' | 'out' = onHand === 0 ? 'out' : onHand <= 5 ? 'low' : 'ok';
  const statusTone = ({ ok: BadgeTone.Success, low: BadgeTone.Warning, out: BadgeTone.Danger } as const)[status];
  const statusLabel = {
    ok:  intl.formatMessage({ id: 'warehouse.inStock', defaultMessage: 'In stock' }),
    low: intl.formatMessage({ id: 'dashboard.lowStock', defaultMessage: 'Low stock' }),
    out: intl.formatMessage({ id: 'warehouse.outOfStock', defaultMessage: 'Out of stock' }),
  }[status];
  const statusBannerBg = { ok: 'var(--success-soft)', low: 'var(--warning-soft)', out: 'var(--danger-soft)' }[status];
  const statusBannerInk = { ok: 'var(--success-ink)', low: 'var(--warning-ink)', out: 'var(--danger-ink)' }[status];
  const statusBannerTitle = {
    ok:  intl.formatMessage({ id: 'warehouse.stockBanner.okTitle' }, { count: onHand }),
    low: intl.formatMessage({ id: 'warehouse.stockBanner.lowTitle' }, { count: onHand }),
    out: intl.formatMessage({ id: 'warehouse.outOfStock', defaultMessage: 'Out of stock' }),
  }[status];
  const statusBannerBody = {
    ok:  intl.formatMessage({ id: 'warehouse.stockBanner.okBody' }),
    low: intl.formatMessage({ id: 'warehouse.stockBanner.lowBody' }),
    out: intl.formatMessage({ id: 'warehouse.stockBanner.outBody' }),
  }[status];

  const wholesale = costPrice ?? 0;
  const retail = sellingPrice ?? 0;
  const margin = wholesale > 0 && retail > 0 ? Math.round((retail - wholesale) / wholesale * 100) : null;

  const formattedCreated = (() => {
    if (!createdOn) return '—';
    const d = new Date(createdOn);
    if (isNaN(d.getTime())) return createdOn;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  })();

  const allImages = [coverImage, ...(images ?? [])].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i);
  const photoSlots = [...allImages.slice(0, 4), ...Array(Math.max(0, 4 - allImages.length)).fill(null)];


  return (
    <>
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 'var(--s-4)' }}>
        <button
          onClick={() => history.push('/warehouse')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '6px 10px', borderRadius: 'var(--r-md)',
            background: 'transparent', border: '1px solid var(--line)',
            color: 'var(--muted)', cursor: 'pointer', fontFamily: 'inherit',
            fontSize: 'var(--t-sm)', fontWeight: 500,
          }}
        >
          <IconChevronLeft style={{ width: 14, height: 14 }} />
          <span>{intl.formatMessage({ id: 'mainNavigation.warehouse', defaultMessage: 'Warehouse' })}</span>
        </button>
        <span style={{ color: 'var(--muted-2)', display: 'flex' }}>
          <IconChevronRight style={{ width: 14, height: 14 }} />
        </span>
        <span style={{ fontSize: 'var(--t-sm)', fontWeight: 600, color: 'var(--ink)' }}>{intl.formatMessage({ id: 'warehouse.product', defaultMessage: 'Product' })}</span>
      </div>

      {/* Header block */}
      <div style={{ marginBottom: 'var(--s-5)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
          <Badge tone={statusTone} dot>{statusLabel}</Badge>
          {categoryName && <Badge tone={BadgeTone.Neutral}><span className="ar">{categoryName}</span></Badge>}
          <span style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>{intl.formatMessage({ id: 'warehouse.updatedJustNow', defaultMessage: 'updated just now' })}</span>
        </div>
        <h1 className="ar" style={{ fontSize: 'var(--t-3xl)', fontWeight: 700, letterSpacing: '-.02em', lineHeight: 1.15, margin: 0 }}>
          {name}
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-3)', marginTop: 8, flexWrap: 'wrap', color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>
          <span style={{ fontFamily: 'var(--font-mono, monospace)', fontVariantNumeric: 'tabular-nums' }}>{sku}</span>
          {specification?.ean && (
            <>
              <span style={{ color: 'var(--muted-2)' }}>·</span>
              <span>{intl.formatMessage({ id: 'warehouse.barcode', defaultMessage: 'Barcode' })} <span style={{ fontFamily: 'var(--font-mono, monospace)' }}>{specification.ean}</span></span>
            </>
          )}
          <span style={{ color: 'var(--muted-2)' }}>·</span>
          <span>{intl.formatMessage({ id: 'warehouse.created', defaultMessage: 'Created' })} {formattedCreated}</span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 'var(--s-4)', flexWrap: 'wrap' }}>
          <Button kind={ButtonKind.Outline} icon={IconEdit} onClick={openEdit}>{intl.formatMessage({ id: 'warehouse.editProduct', defaultMessage: 'Edit product' })}</Button>
          <Button kind={ButtonKind.Primary} icon={IconArrowForward} onClick={() => history.push(generatePath(routes.PLACE_PRODUCT_ORDER, { productId: product.id }))}>{intl.formatMessage({ id: 'warehouse.productCardButtonLabel', defaultMessage: 'Create order' })}</Button>
        </div>
      </div>

      {/* Status banner */}
      <div style={{
        padding: 'var(--s-5)', marginBottom: 'var(--s-5)', borderRadius: 'var(--r-lg)',
        background: statusBannerBg, border: `1px solid ${statusBannerBg}`,
        display: 'grid', gridTemplateColumns: '48px 1fr auto', gap: 16, alignItems: 'center',
        boxShadow: 'var(--sh-1)',
      }}>
        <div style={{ width: 48, height: 48, borderRadius: 'var(--r-md)', background: statusBannerBg, color: statusBannerInk, display: 'grid', placeItems: 'center', fontSize: 22 }}>
          {status === 'ok' ? '✓' : '⚠'}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 'var(--t-lg)', color: statusBannerInk }}>{statusBannerTitle}</div>
          <div style={{ fontSize: 'var(--t-sm)', color: statusBannerInk, opacity: 0.85, marginTop: 4, maxWidth: 680 }}>{statusBannerBody}</div>
        </div>
      </div>

      {/* Main two-column grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 'var(--s-5)', alignItems: 'start' }}>

        {/* LEFT column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-4)', minWidth: 0 }}>

          {/* Gallery + Details */}
          <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 'var(--s-4)' }}>
            {/* Gallery */}
            <ScCard pad={false} style={{ padding: 'var(--s-4)' }}>
              <div style={{ aspectRatio: '5/4', borderRadius: 'var(--r-md)', background: '#FCE4EC', display: 'grid', placeItems: 'center', color: 'rgba(0,0,0,.18)', position: 'relative', overflow: 'hidden' }}>
                {allImages[0] ? (
                  <img src={allImages[0]} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <ProductMonogram name={name} scale={1.5} />
                )}
                <input
                  ref={uploadInputRef}
                  type='file'
                  accept='image/png,image/jpeg'
                  style={{ display: 'none' }}
                  onChange={onCoverImageChange}
                />
                <button
                  type='button'
                  title={intl.formatMessage({ id: 'warehouse.uploadPhoto', defaultMessage: 'Upload photo' })}
                  disabled={uploadingImage}
                  onClick={() => uploadInputRef.current?.click()}
                  style={{ position: 'absolute', top: 10, right: 10, width: 34, height: 34, borderRadius: 'var(--r-sm)', border: 0, background: 'rgba(255,255,255,.92)', color: 'var(--ink-2)', cursor: uploadingImage ? 'wait' : 'pointer', opacity: uploadingImage ? 0.6 : 1, display: 'grid', placeItems: 'center', backdropFilter: 'blur(4px)' }}
                >
                  <IconUpload style={{ width: 16, height: 16 }} />
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginTop: 10 }}>
                {photoSlots.map((src, i) => (
                  <button key={i} style={{ aspectRatio: '1/1', borderRadius: 'var(--r-sm)', background: ['#FCE4EC','#FBE9F0','#FFF1F4','#FDE7EB'][i] ?? 'var(--paper-2)', border: i === 0 ? '2px solid var(--brand-green)' : '1px solid var(--line)', padding: 0, cursor: 'pointer', display: 'grid', placeItems: 'center', color: 'rgba(0,0,0,.18)', overflow: 'hidden' }}>
                    {src ? <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <IconBox style={{ width: 18, height: 18 }} />}
                  </button>
                ))}
              </div>
            </ScCard>

            {/* Details card */}
            <ScCard pad={false} style={{ padding: 'var(--s-5)' }}>
              <ScSectionHeader title={intl.formatMessage({ id: 'warehouse.details', defaultMessage: 'Details' })} icon={<IconInfo style={{ width: 16, height: 16 }} />} />
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <KV k={intl.formatMessage({ id: 'warehouse.englishName', defaultMessage: 'English name' })} v={name} />
                <KV k={intl.formatMessage({ id: 'warehouse.category', defaultMessage: 'Category' })} v={<span className="ar">{categoryName}</span>} />
                <KV k={intl.formatMessage({ id: 'warehouse.businessType', defaultMessage: 'Business type' })} v="—" />
                <KV k={intl.formatMessage({ id: 'warehouse.supplier', defaultMessage: 'Supplier' })} v="—" />
                {specification?.unitName && <KV k={intl.formatMessage({ id: 'warehouse.unit', defaultMessage: 'Unit' })} v={specification.unitName} />}
                <KV k={intl.formatMessage({ id: 'warehouse.description', defaultMessage: 'Description' })} v={<span style={{ maxWidth: 180, textAlign: 'right', fontSize: 12, color: 'var(--muted)' }}>{description?.slice(0, 60) || '—'}</span>} last />
              </div>
            </ScCard>
          </div>

          {/* KPI strip */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--s-3)' }}>
            <ScStat label={intl.formatMessage({ id: 'warehouse.remaining', defaultMessage: 'Remaining' })} value={onHand} sub={intl.formatMessage({ id: 'warehouse.thresholdSub', defaultMessage: 'Threshold · 5 units' }, { threshold: 5 })} accent={status === 'ok' ? 'green' : status === 'low' ? 'warning' : 'danger'} />
            <ScStat label={intl.formatMessage({ id: 'warehouse.ordered', defaultMessage: 'Ordered' })} value="—" sub={intl.formatMessage({ id: 'warehouse.acrossAllEntities', defaultMessage: 'Across all entities' })} accent="ink" />
            <ScStat label={intl.formatMessage({ id: 'warehouse.damaged', defaultMessage: 'Damaged' })} value="—" sub={intl.formatMessage({ id: 'warehouse.pendingWriteoff', defaultMessage: 'Pending writeoff' })} accent="ink" />
            <ScStat label={intl.formatMessage({ id: 'warehouse.avgDailySold', defaultMessage: 'Avg daily sold' })} value="—" sub={intl.formatMessage({ id: 'warehouse.last14days', defaultMessage: 'Last 14 days' })} accent="green" />
          </div>

          {/* Sales analytics — coming soon */}
          <ScCard pad={false} style={{ padding: 'var(--s-6)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, minHeight: 160, textAlign: 'center' }}>
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', background: 'var(--paper-2)', color: 'var(--muted)', border: '1px solid var(--line-2)', borderRadius: 'var(--r-sm)', padding: '2px 8px' }}>
              {intl.formatMessage({ id: 'comingSoon.short', defaultMessage: 'Coming soon' })}
            </span>
            <div style={{ fontWeight: 700, fontSize: 'var(--t-lg)', color: 'var(--ink)' }}>
              {intl.formatMessage({ id: 'warehouse.analyticsTitle', defaultMessage: 'Sales analytics' })}
            </div>
            <div style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)', maxWidth: 320 }}>
              {intl.formatMessage({ id: 'warehouse.analyticsDesc', defaultMessage: 'Sales trend, by-entity breakdown, and recent orders will appear here once the analytics endpoint is available.' })}
            </div>
          </ScCard>
        </div>

        {/* RIGHT rail — sticky */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-4)', minWidth: 0, position: 'sticky', top: 'calc(var(--topbar-h, 64px) + 24px)' }}>

          {/* Pricing */}
          <ScCard pad={false} style={{ padding: 'var(--s-5)' }}>
            <ScSectionHeader
              title={intl.formatMessage({ id: 'warehouse.pricing', defaultMessage: 'Pricing' })}
              actions={<Button kind={ButtonKind.Ghost} icon={IconEdit} onClick={openEdit}>{intl.formatMessage({ id: 'common.edit', defaultMessage: 'Edit' })}</Button>}
            />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <KV k={intl.formatMessage({ id: 'warehouse.wholesale', defaultMessage: 'Wholesale' })} v={<><span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{wholesale.toLocaleString()}</span> <span style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>IQD</span></>} />
              <KV k={intl.formatMessage({ id: 'warehouse.retail', defaultMessage: 'Retail' })} v={<><span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{retail.toLocaleString()}</span> <span style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>IQD</span></>} />
              <KV k={intl.formatMessage({ id: 'warehouse.margin', defaultMessage: 'Margin' })} v={<span style={{ color: 'var(--success-ink)', fontWeight: 600 }}>{margin !== null ? `+${margin}%` : '—'}</span>} last />
            </div>
          </ScCard>

          {/* Stock */}
          <ScCard pad={false} style={{ padding: 'var(--s-5)' }}>
            <ScSectionHeader
              title={intl.formatMessage({ id: 'warehouse.stock', defaultMessage: 'Stock' })}
              icon={<IconWarehouse style={{ width: 16, height: 16 }} />}
            />
            <div style={{ padding: '14px 16px', borderRadius: 'var(--r-md)', background: 'var(--paper)', display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 'var(--t-3xl)', fontWeight: 700, letterSpacing: '-.02em', fontVariantNumeric: 'tabular-nums', color: status === 'ok' ? 'var(--brand-green)' : status === 'low' ? 'var(--warning)' : 'var(--danger)' }}>
                {onHand}
              </span>
              <span style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>{intl.formatMessage({ id: 'warehouse.unitsLeft', defaultMessage: 'units left' })}</span>
            </div>
            <Row2sm label={intl.formatMessage({ id: 'warehouse.reservedInCarts', defaultMessage: 'Reserved (in carts)' })} value="—" />
            <Row2sm label={intl.formatMessage({ id: 'warehouse.incoming', defaultMessage: 'Incoming' })} value="—" />
            <Row2sm label={intl.formatMessage({ id: 'warehouse.reorderThreshold', defaultMessage: 'Reorder threshold' })} value={intl.formatMessage({ id: 'warehouse.reorderThresholdValue', defaultMessage: '≤ {threshold} units' }, { threshold: 5 })} />
            <Row2sm label={intl.formatMessage({ id: 'warehouse.estDaysToZero', defaultMessage: 'Est. days to zero' })} value="—" last />
          </ScCard>

          {/* Activity timeline */}
          <ScCard pad={false} style={{ padding: 'var(--s-5)' }}>
            <ScSectionHeader title={intl.formatMessage({ id: 'warehouse.activity', defaultMessage: 'Activity' })} subtitle={intl.formatMessage({ id: 'warehouse.activitySubtitle', defaultMessage: 'Most recent first' })} />
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', left: 7, top: 8, bottom: 8, width: 2, background: 'var(--line)' }} />
              {TIMELINE_MOCK.map((ev, i) => {
                const c = TONE_COLOR[ev.tone] || 'var(--muted-2)';
                return (
                  <div key={i} style={{ position: 'relative', paddingLeft: 24, paddingBottom: 14 }}>
                    <span style={{ position: 'absolute', left: 0, top: 2, width: 16, height: 16, borderRadius: 8, background: 'var(--surface)', display: 'grid', placeItems: 'center' }}>
                      <span style={{ width: 10, height: 10, borderRadius: 5, background: c, boxShadow: ev.active ? `0 0 0 3px ${c}33` : 'none' }} />
                    </span>
                    <div style={{ fontSize: 'var(--t-sm)', fontWeight: 600, color: ev.active ? c : 'var(--ink)' }}>{ev.label}</div>
                    <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 2 }}>{ev.sub}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted-2)', fontVariantNumeric: 'tabular-nums', marginTop: 3 }}>{ev.date}</div>
                  </div>
                );
              })}
            </div>
          </ScCard>
        </div>
      </div>

    </>
  );
};

export default ProductDetails;
